/* eslint-disable @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-argument */
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

import { PagesData } from '@shared/models/pages-data.model'
import { ApiService } from '@shared/services/api.service'
import { withFeedback } from '@app/core/interceptors/feedback-context'
import { Reseller, ResellerUser } from '@shared/models/reseller.model'
import { FilterValues } from '@shared/models/filter-values.model'
import { ResellerFormSave } from '../models/reseller-form-save.model'
import { NoteHistory } from '@shared/models/note.model'
import { Client } from '@shared/models/client.model'
import { User } from '@shared/models/user.model'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { toFormData } from '@core/helpers/to-form-data'
import { History } from '@shared/models/history.model'

@Injectable({ providedIn: 'root' })
export class ResellerService {
  constructor(private apiService: ApiService) {}

  getResellers(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Reseller)[],
  ): Observable<PagesData<Reseller>> {
    // TODO: switch when Go supports paginated list
    return this.apiService.getPageable<Reseller>('/reseller', pageIndex, pageSize, sort, filterValues, fields)
  }

  addReseller(reseller: ResellerFormSave): Observable<Reseller> {
    return this.apiService.post(`/reseller`, reseller, { context: withFeedback() }).pipe(map((resp) => resp['data']))
  }

  updateReseller(id: string | number, reseller: ResellerFormSave): Observable<Reseller> {
    return this.apiService.post(`/reseller/${id}`, reseller, { context: withFeedback() }).pipe(map((resp) => resp['data']))
  }

  getResellerById(id: number | string): Observable<Reseller> {
    return this.apiService.get(`/reseller/${id}`).pipe(map((resp) => new Reseller(resp['data'])))
  }

  getResellerUsers(
    id: number | string,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof ResellerUser)[],
  ): Observable<PagesData<ResellerUser>> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // TODO: switch when Go supports paginated list
    return this.apiService.getPageable<any>(`/reseller/${id}/users`, pageIndex, pageSize, sort, filterValues, fields)
  }

  addResellerUser(id: number | string, user: Partial<ResellerUser>): Observable<ResellerUser> {
    return this.apiService.post(`/reseller/${id}/users`, user, { context: withFeedback() }).pipe(map((resp) => resp['data']))
  }

  getResellerUserById(resellerId: number | string, userId: string | number): Observable<ResellerUser> {
    return this.apiService.get(`/reseller/${resellerId}/users/${userId}`).pipe(map((resp) => new ResellerUser(resp['data'])))
  }

  editResellerUser(resellerId: number | string, userId: number | string, user: Partial<ResellerUser>): Observable<ResellerUser> {
    return this.apiService
      .post(`/reseller/${resellerId}/users/${userId}`, user, { context: withFeedback() })
      .pipe(map((resp) => resp['data']))
  }

  deleteResellerUser(resellerId: number | string, userId: number | string): Observable<void> {
    return this.apiService.delete(`/admin/users/${userId}`, { context: withFeedback() })
  }

  restoreUser(id: string | number): Observable<User> {
    return this.apiService.post(`/users/${id}/restore`, {}, { context: withFeedback() })
  }

  getResellerNotes(resellerId: number | string, note: string): Observable<NoteHistory[]> {
    return this.apiService.get(`/reseller-notes/${resellerId}/${note}`).pipe(map((resp) => resp['data']))
  }

  addResellertNote(id: number | string, note: string, noteType: 'admin' | 'reseller'): Observable<void> {
    const body = {
      [`${noteType}Note`]: note,
    }

    return this.apiService.post(`/reseller/${id}/notes`, body, { context: withFeedback() })
  }

  deleteResellertNote(id: number | string): Observable<void> {
    return this.apiService.delete(`/notes/${id}`, { context: withFeedback() })
  }

  getResellerStatusHistory(id: number | string): Observable<History[]> {
    return this.apiService.get(`/reseller/${id}/history/status`).pipe(map((resp) => resp['data']))
  }

  getResellerClients(
    resellerId: string | number,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Client)[],
  ): Observable<PagesData<Client>> {
    // TODO: switch when Go supports paginated list
    return this.apiService.getPageable<Client>(`/reseller/${resellerId}/clients/json`, pageIndex, pageSize, sort, filterValues, fields)
  }

  getResellerClientsList(
    resellerId: string | number,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Client)[],
  ): Observable<Client[]> {
    return this.getResellerClients(resellerId, pageIndex, pageSize, sort, filterValues, fields).pipe(
      map((response) => response.data.map((data) => new Client(data))),
    )
  }

  exportResellerClients(path: string, columns: string[], sort: string, filterValues: FilterValues): Observable<string> {
    return this.apiService.exportFile(path, columns, sort, filterValues)
  }

  updatePlatformSettings(resellerId: string | number, formdata): Observable<PlatformSettings> {
    return this.apiService.post(`/reseller/${resellerId}/platform-settings`, toFormData(formdata, null, null, ['logo', 'favicon']), {
      context: withFeedback(),
    })
  }

  getPlatformSettings(resellerId: string | number): Observable<PlatformSettings> {
    return this.apiService.get(`/reseller/${resellerId}/platform-settings`).pipe(map((resp) => resp['data']))
  }
}
